<script setup lang="ts" generic="StepData extends { navigation?: boolean }">
type StepDefs<data> = Record<number, data>

interface Props {
  modelValue: number
  steps: StepDefs<StepData>
  emitedNavigation?: boolean
  submitLabel?: string
  finalizeAcction?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  submitLabel: 'Finalizar',
  finalizeAcction: true,
})

const emits = defineEmits(['update:modelValue', 'nextStep', 'previousStep', 'end'])
const { modelValue: model } = useVModels(props, emits)

const goToStep = (step: number) => {
  model.value = step
}

const isFirstStep = computed(() => {
  const firstStep = Object.keys(props.steps)[0]
  return firstStep === model.value.toString()
})

const isLastStep = computed(() => {
  const stepsList = Object.keys(props.steps)
  const lasteStep = stepsList[stepsList.length - 1]
  return lasteStep === model.value.toString()
})

const nextStep = () => {
  if (props.emitedNavigation) emits('nextStep')
  else {
    const stepsList = Object.keys(props.steps)
    const value = stepsList[stepsList.indexOf(`${model.value}`) + 1]
    if (value) model.value = +value
  }
}

const previousStep = () => {
  if (props.emitedNavigation) emits('previousStep')
  else {
    const stepsList = Object.keys(props.steps)
    const value = stepsList[stepsList.indexOf(`${model.value}`) - 1]
    if (value) model.value = +value
  }
}
const isDevelopment = computed(() => process.env['NODE_ENV'] === 'development')
</script>

<template>
  <PvStepper v-model:value="model">
    <div class="flex flex-col gap-6 lg:gap-10">
      <CardComponent bg="light">
        <PvStepList>
          <template
            v-for="(_, step, index) in steps"
            :key="step"
          >
            <PvStep
              as-child
              :value="step"
            >
              <div
                v-if="+step !== Math.min(...Object.keys(steps).map((step) => +step))"
                class="mx-2 w-full"
              >
                <hr class="border-primary-100/10" />
              </div>
              <button @click="() => (isDevelopment ? goToStep(+step) : undefined)">
                <span
                  :class="[
                    'inline-flex size-5 md:size-6 items-center justify-center rounded-full leading-none text-xs md:text-base',
                    {
                      'bg-white text-primary font-bold': +step <= model,
                      'bg-primary-100/10': +step > model,
                    },
                  ]"
                >
                  {{ index + 1 }}
                </span>
              </button>
            </PvStep>
          </template>
        </PvStepList>
      </CardComponent>
      <PvStepPanels>
        <template
          v-for="(data, step) in steps"
          :key="step"
        >
          <PvStepPanel
            :value="+step"
            class="flex flex-col gap-10"
          >
            <div class="flex flex-col gap-6">
              <slot
                :name="`step-${step}`"
                :step="step"
                :data="data"
              />
            </div>
            <div
              v-if="!isFirstStep || data.navigation !== false"
              class="flex justify-between"
            >
              <PvButton
                v-if="!isFirstStep"
                severity="contrast"
                outlined
                class="text-sm md:text-base"
                @click="previousStep()"
              >
                <span class="icon-[ph--caret-double-left-bold]" />
                Regresar
              </PvButton>

              <PvButton
                v-if="data.navigation !== false && (finalizeAcction || !isLastStep)"
                :severity="isLastStep ? 'success' : 'contrast'"
                class="text-sm md:text-base"
                @click="isLastStep ? emits('end') : nextStep()"
              >
                <span class="flex items-center gap-2 font-bold">
                  <span
                    v-if="isLastStep"
                    class="icon-[ph--shield-check-bold]"
                  />
                  {{ isLastStep ? (submitLabel ?? 'Enviar') : 'Continuar' }}
                  <span
                    v-if="!isLastStep"
                    class="icon-[ph--caret-double-right-bold]"
                  />
                </span>
              </PvButton>
            </div>
          </PvStepPanel>
        </template>
      </PvStepPanels>
    </div>
  </PvStepper>
</template>
